<template>
  <Teleport to="body">
    <!-- Glavni wrapper sa animacijom -->
    <Transition
      enter-active-class="duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="alertState.isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click="handleOverlayClick"
      >
        <!-- Backdrop sa blur -->
        <div class="absolute inset-0 bg-gray-950/40 backdrop-blur-md" />

        <!-- Modal sa animacijom -->
        <Transition
          enter-active-class="duration-300 ease-out"
          enter-from-class="opacity-0 scale-90 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="duration-200 ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-90 translate-y-4"
        >
          <div
            v-if="alertState.isOpen"
            :class="[
              'relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl',
              'border border-gray-200 dark:border-gray-800',
              'overflow-hidden',
              sizeClasses[alertState.size],
              'w-full max-h-[90vh] flex flex-col',
            ]"
            @click.stop
          >
            <!-- Progress Bar (ako postoji) -->
            <div
              v-if="alertState.showProgressBar && alertState.timer"
              class="absolute top-0 left-0 right-0 h-1 bg-gray-100 dark:bg-gray-800"
            >
              <div
                :class="[
                  'h-full transition-all duration-100',
                  progressColorClass,
                ]"
                :style="{ width: `${alertState.progress}%` }"
              />
            </div>

            <!-- Close Button (ako nije persistent) -->
            <UButton
              variant="ghost"
              icon="i-lucide-x"
              size="sm"
              aria-label="Close"
              v-if="!alertState.persistent && !alertState.loading"
              @click="cancelAlert"
              class="absolute top-4 right-4 z-10 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
            </UButton>

            <!-- Content -->
            <div class="flex flex-col items-center text-center px-8 py-8">
              <!-- Icon -->
              <div v-if="alertState.icon" class="mb-6 relative">
                <!-- Glow efekat -->
                <div
                  :class="[
                    'absolute inset-0 rounded-full blur-xl opacity-20',
                    glowColorClass,
                  ]"
                />

                <!-- Glavni ring -->
                <div
                  :class="[
                    'relative rounded-full ring-8',
                    'w-24 h-24',
                    'flex items-center justify-center',
                    iconContainerClass,
                  ]"
                >
                  <UIcon
                    :name="alertState.icon"
                    :class="['w-12 h-12', iconColorClass]"
                  />
                </div>
              </div>

              <!-- Title -->
              <h3
                v-if="alertState.title"
                class="text-2xl font-bold text-gray-900 dark:text-white mb-3"
              >
                {{ alertState.title }}
              </h3>

              <!-- Message -->
              <div v-if="alertState.message" class="mb-8">
                <div
                  v-if="alertState.html"
                  class="text-base text-gray-600 dark:text-gray-400 leading-relaxed"
                  v-html="alertState.message"
                />
                <p
                  v-else
                  class="text-base text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line"
                >
                  {{ alertState.message }}
                </p>
              </div>

              <!-- Loading State -->
              <div
                v-if="alertState.loading"
                class="flex flex-col items-center gap-4"
              >
                <div class="relative w-16 h-16">
                  <div
                    class="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-800"
                  />
                  <div
                    :class="[
                      'absolute inset-0 rounded-full border-4 border-t-transparent animate-spin',
                      spinnerColorClass,
                    ]"
                  />
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ alertState.message || 'Molimo sačekajte...' }}
                </p>
              </div>

              <!-- Buttons -->
              <div
                v-if="!alertState.loading"
                :class="[
                  'flex gap-3 w-full',
                  alertState.showCancel ? 'flex-row-reverse' : 'justify-center',
                ]"
              >
                <!-- Confirm Button -->
                <button
                  @click="confirmAlert"
                  :class="[
                    'flex-1 px-6 py-3 rounded-xl font-semibold text-white',
                    'transition-all duration-200',
                    'hover:scale-105 active:scale-95',
                    'shadow-lg hover:shadow-xl',
                    confirmButtonClass,
                  ]"
                >
                  {{ alertState.confirmText }}
                </button>

                <!-- Cancel Button -->
                <button
                  v-if="alertState.showCancel"
                  @click="cancelAlert"
                  class="flex-1 px-6 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {{ alertState.cancelText }}
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useAlert } from '~/composables/useAlert'

const { alertState, confirmAlert, cancelAlert, closeAlert } = useAlert()

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
}

// Ikona container sa ring efektom
const iconContainerClass = computed(() => {
  const classes = {
    success:
      'bg-success-50 dark:bg-success-950/20 ring-success-100 dark:ring-success-900/30',
    error:
      'bg-error-50 dark:bg-error-950/20 ring-error-100 dark:ring-error-900/30',
    warning:
      'bg-warning-50 dark:bg-warning-950/20 ring-warning-100 dark:ring-warning-900/30',
    info: 'bg-info-50 dark:bg-info-950/20 ring-info-100 dark:ring-info-900/30',
    primary:
      'bg-primary-50 dark:bg-primary-950/20 ring-primary-100 dark:ring-primary-900/30',
    secondary: 'bg-gray-100 dark:bg-gray-800 ring-gray-200 dark:ring-gray-700',
    neutral: 'bg-gray-100 dark:bg-gray-800 ring-gray-200 dark:ring-gray-700',
  }
  return classes[alertState.color] || classes.primary
})

// Boja ikone
const iconColorClass = computed(() => {
  const classes = {
    success: 'text-success-600 dark:text-success-400',
    error: 'text-error-600 dark:text-error-400',
    warning: 'text-warning-600 dark:text-warning-400',
    info: 'text-info-600 dark:text-info-400',
    primary: 'text-primary-600 dark:text-primary-400',
    secondary: 'text-gray-600 dark:text-gray-400',
    neutral: 'text-gray-600 dark:text-gray-400',
  }
  return classes[alertState.color] || classes.primary
})

// Confirm button klase sa gradient efektom
const confirmButtonClass = computed(() => {
  const classes = {
    success:
      'bg-gradient-to-r from-success-500 to-success-600 hover:from-success-600 hover:to-success-700',
    error:
      'bg-gradient-to-r from-error-500 to-error-600 hover:from-error-600 hover:to-error-700',
    warning:
      'bg-gradient-to-r from-warning-500 to-warning-600 hover:from-warning-600 hover:to-warning-700',
    info: 'bg-gradient-to-r from-info-500 to-info-600 hover:from-info-600 hover:to-info-700',
    primary:
      'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700',
    secondary:
      'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700',
    neutral:
      'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700',
  }
  return classes[alertState.color] || classes.primary
})

// Progress bar boja
const progressColorClass = computed(() => {
  const classes = {
    success: 'bg-gradient-to-r from-success-400 to-success-600',
    error: 'bg-gradient-to-r from-error-400 to-error-600',
    warning: 'bg-gradient-to-r from-warning-400 to-warning-600',
    info: 'bg-gradient-to-r from-info-400 to-info-600',
    primary: 'bg-gradient-to-r from-primary-400 to-primary-600',
    secondary: 'bg-gradient-to-r from-gray-400 to-gray-600',
    neutral: 'bg-gradient-to-r from-gray-400 to-gray-600',
  }
  return classes[alertState.color] || classes.primary
})

// Spinner boja
const spinnerColorClass = computed(() => {
  const classes = {
    success: 'border-success-500',
    error: 'border-error-500',
    warning: 'border-warning-500',
    info: 'border-info-500',
    primary: 'border-primary-500',
    secondary: 'border-gray-500',
    neutral: 'border-gray-500',
  }
  return classes[alertState.color] || classes.primary
})

const glowColorClass = computed(() => {
  const classes = {
    success: 'bg-success-500',
    error: 'bg-error-500',
    warning: 'bg-warning-500',
    info: 'bg-info-500',
    primary: 'bg-primary-500',
    secondary: 'bg-gray-500',
    neutral: 'bg-gray-500',
  }
  return classes[alertState.color] || classes.primary
})

const handleOverlayClick = () => {
  if (
    alertState.allowOutsideClick &&
    !alertState.persistent &&
    !alertState.loading
  ) {
    closeAlert(false)
  }
}

const handleEscape = (event: KeyboardEvent) => {
  if (
    event.key === 'Escape' &&
    alertState.allowEscapeKey &&
    !alertState.persistent &&
    !alertState.loading
  ) {
    closeAlert(false)
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>
